import type {
  CatalogCategory,
  CatalogItem,
  CatalogLanguage,
  CatalogPrice,
  CatalogSnapshotV1,
} from "./types";

export function catalogSlug(pathname: string): string | null {
  const match = pathname.match(/^\/c\/([^/]+)\/?$/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function parseCatalogSnapshot(payload: unknown): CatalogSnapshotV1 {
  const parsed =
    typeof payload === "string" ? (JSON.parse(payload) as unknown) : payload;
  if (
    !parsed ||
    typeof parsed !== "object" ||
    (parsed as { schemaVersion?: unknown }).schemaVersion !== 1 ||
    !Array.isArray((parsed as { items?: unknown }).items) ||
    !Array.isArray((parsed as { categories?: unknown }).categories)
  ) {
    throw new Error("Unsupported catalog snapshot.");
  }
  return parsed as CatalogSnapshotV1;
}

export function defaultLanguage(languages: readonly string[]): CatalogLanguage {
  return languages.some((language) => language.toLowerCase().startsWith("ar"))
    ? "ar"
    : "en";
}

export function localized(
  english: string | undefined,
  arabic: string | undefined,
  language: CatalogLanguage,
): string {
  return language === "ar"
    ? arabic?.trim() || english?.trim() || ""
    : english?.trim() || arabic?.trim() || "";
}

export function categoryName(
  category: CatalogCategory,
  language: CatalogLanguage,
): string {
  return localized(category.name, category.nameAr, language);
}

export function filterCatalogItems(args: {
  items: CatalogItem[];
  categories: CatalogCategory[];
  language: CatalogLanguage;
  search: string;
  categoryId: string | null;
}): CatalogItem[] {
  const query = args.search.trim().toLocaleLowerCase(args.language);
  const categoryById = new Map(
    args.categories.map((category) => [category.id, category]),
  );
  return args.items.filter((item) => {
    if (args.categoryId && item.categoryId !== args.categoryId) return false;
    if (!query) return true;
    const category = item.categoryId
      ? categoryById.get(item.categoryId)
      : undefined;
    return [
      item.name,
      item.nameAr,
      item.description,
      item.descriptionAr,
      category?.name,
      category?.nameAr,
    ].some((value) => value?.toLocaleLowerCase(args.language).includes(query));
  });
}

export function formatPrice(
  price: CatalogPrice | undefined,
  language: CatalogLanguage,
): string {
  if (!price) return language === "ar" ? "السعر عند الطلب" : "Price on request";
  if (price.mode === "quote_required") {
    return (
      price.label ||
      (language === "ar" ? "السعر عند الطلب" : "Price on request")
    );
  }
  if (price.mode === "label_only") return price.label || "—";
  if (!("amount" in price)) return "—";
  const number = new Intl.NumberFormat(language === "ar" ? "ar-IQ" : "en-US", {
    maximumFractionDigits: 2,
  }).format(price.amount);
  const prefix =
    price.mode === "starting_at"
      ? language === "ar"
        ? "ابتداءً من "
        : "From "
      : "";
  return `${prefix}${number} ${price.currency}`;
}

export function updateCatalogHeadMetadata(
  snapshot: CatalogSnapshotV1,
  language: CatalogLanguage,
  imageUrl: (fileId: string, width: number) => string,
): void {
  if (typeof document === "undefined") return;

  const title = localized(
    snapshot.branding.title,
    snapshot.branding.titleAr,
    language,
  );
  const pageTitle = title ? `${title} | Sela` : "Sela Catalog";
  document.title = pageTitle;

  const description =
    snapshot.kind === "restaurant"
      ? language === "ar"
        ? `قائمة الطعام والوجبات لـ ${title}`
        : `Explore menu and dishes from ${title} on Sela.`
      : language === "ar"
        ? `كتالوج المنتجات والمعروضات لـ ${title}`
        : `Browse catalog and products from ${title} on Sela.`;

  let faviconUrl = "";
  let ogImageUrl = "";

  if (snapshot.branding.logo?.fileId) {
    faviconUrl = imageUrl(snapshot.branding.logo.fileId, 256);
    ogImageUrl = imageUrl(snapshot.branding.logo.fileId, 1200);
  }

  const updateTag = (
    selector: string,
    attributeName: "content" | "href",
    value: string,
  ) => {
    if (!value) return;
    const element = document.querySelector(selector);
    if (element) {
      element.setAttribute(attributeName, value);
    }
  };

  updateTag('#meta-description, meta[name="description"]', "content", description);
  updateTag('#og-title, meta[property="og:title"]', "content", pageTitle);
  updateTag('#og-description, meta[property="og:description"]', "content", description);
  updateTag('#twitter-title, meta[name="twitter:title"]', "content", pageTitle);
  updateTag('#twitter-description, meta[name="twitter:description"]', "content", description);

  if (faviconUrl) {
    updateTag('#dynamic-favicon, link[rel="icon"]', "href", faviconUrl);
    updateTag('#dynamic-apple-icon, link[rel="apple-touch-icon"]', "href", faviconUrl);
  }

  if (ogImageUrl) {
    updateTag('#og-image, meta[property="og:image"]', "content", ogImageUrl);
    updateTag('#twitter-image, meta[name="twitter:image"]', "content", ogImageUrl);
  }
}

